/**
 * RC-152c-2a + RC-152c-2a-style — Form Designer · field palette
 * (left column).
 *
 * Renders the field-type registry from {@code _shared/fieldTypes.js}
 * as a category-grouped list of clickable items. Clicking an item
 * calls {@code onAppendField(type)} which the parent
 * {@link FormDesignerPage} routes through the composition reducer
 * to append a new field with sensible defaults.
 *
 * <p><b>Style contract (RC-152c-2a-style operator directive,
 * 2026-06-21):</b> All visual styling comes from V2 design tokens
 * via Tailwind utility classes ({@code bg-v2-muted},
 * {@code border-v2-border-subtle}, {@code text-v2-ink-*}). No
 * inline {@code style={{}}} objects, no hand-rolled hex, no
 * literal CSS variables embedded in JS strings.</p>
 *
 * <p><b>Click vs drag-drop:</b> Click-to-append only; native HTML5
 * drag-drop arrives in a follow-up phase.</p>
 *
 * <p><b>Read-only:</b> when {@code readOnly} is set, every item
 * still renders but the click handler short-circuits. Operators see
 * what types are available even when they can't edit.</p>
 */

import React from 'react';
import {
  Type, AlignLeft, Hash, Calendar, Clock,
  ChevronDown, ListChecks, Check, Upload,
} from 'lucide-react';
import { FIELD_TYPES, FIELD_CATEGORIES } from './_shared/fieldTypes';

/* lucide-react doesn't expose icons by string name at runtime, so
 * the registry stores icon name strings and we resolve to the
 * actual components here. Adding a new icon requires (a) importing
 * it above and (b) extending this map. Two-step keeps tree-shaking
 * effective (unused icons don't bloat the bundle). */
const ICONS = {
  Type, AlignLeft, Hash, Calendar, Clock,
  ChevronDown, ListChecks, Check, Upload,
};

export default function FieldPalette({ onAppendField, readOnly = false }) {
  // Group registry entries by category in render-loop order so the
  // section header / item layout stays declarative.
  const byCategory = FIELD_CATEGORIES.map((cat) => ({
    category: cat,
    types: FIELD_TYPES.filter((t) => t.category === cat),
  }));

  return (
    <aside
      aria-label="Field palette"
      className="flex flex-col gap-3.5 p-3 bg-v2-muted border-r border-v2-border-subtle overflow-y-auto"
    >
      {byCategory.map((group) => (
        <div key={group.category}>
          <div className="text-[10px] text-v2-ink-muted uppercase tracking-wide font-semibold mb-2 px-1.5">
            {group.category}
          </div>
          <div className="flex flex-col gap-1">
            {group.types.map((t) => {
              const Icon = ICONS[t.icon];
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => { if (!readOnly) onAppendField(t.value); }}
                  disabled={readOnly}
                  title={t.helpText}
                  className={
                    'v2-focus-ring flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-left ' +
                    'bg-v2-surface border border-dashed border-v2-border rounded-v2-md text-v2-ink ' +
                    'hover:border-v2-action hover:bg-v2-muted ' +
                    'disabled:cursor-not-allowed disabled:opacity-55'
                  }
                >
                  {Icon && <Icon size={14} aria-hidden="true" className="text-v2-ink-secondary" />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="mt-auto px-1.5 pt-1.5 text-[10px] text-v2-ink-muted leading-snug">
        Click a type to add a new field. Reorder fields from the canvas in the center.
      </div>
    </aside>
  );
}
